/**
 * Esecuzioni — the LLM run-log. Every LLM call (Ask, Odino, voice, chat, squadre)
 * lands one row in the `ai_usage` ledger; the "Costi AI" page shows the aggregates,
 * THIS page is the per-turn detail (one row per agent turn). Read-only.
 *
 * The surface filter is DRIVEN BY the API (`surfaces` on the response), never
 * hardcoded — a surface is a free string (`ask` / `odino` / `voice` / `chat` /
 * `team:<key>`), so new ones appear with zero FE change. The actor id is shown raw
 * and never linked: the realm ("operatore" vs "app user") is inferred from the
 * surface, not resolved. Under the `ai` macro-area.
 *
 * Native `<select>` is intentional: this is the operator console.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/cn";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { type ExecutionRow, executionsApi } from "@/lib/api-ai";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";

const WINDOWS = [7, 30, 90] as const;
const PAGE_SIZES = [25, 50, 100] as const;

/** The realm an actor id belongs to, inferred from the surface family. The portal
 *  surfaces (voice/chat) are app users; the operator surfaces (ask/odino/team) are
 *  operators. Used only as a LABEL — the id itself is never linked or resolved. */
function realmLabel(surface: string): string {
  if (surface === "voice" || surface === "chat") return "app user";
  return "operatore";
}

/** A surface family → a faint colour-tint, so the eye groups runs at a glance. The
 *  family is the part before any `:` (e.g. `team:sales` → `team`). */
function surfaceTint(surface: string): string {
  const family = surface.split(":")[0];
  switch (family) {
    case "ask":
      return "bg-accent/10 text-accent";
    case "odino":
      return "bg-info/10 text-info";
    case "voice":
      return "bg-success/10 text-success";
    case "chat":
      return "bg-warning/10 text-warning";
    case "team":
      return "bg-accent/15 text-accent-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function num(n: number): string {
  return n.toLocaleString("it-IT");
}

function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT");
}

export function ExecutionsPage() {
  // This page is read-only (the `odino:use` gate is enforced server-side); there are
  // no write controls, so no permission hook is needed.

  // Filters. Changing any of them resets to page 1 (a filtered total != the unfiltered
  // one, so the old page index can fall out of range).
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const [surface, setSurface] = useState<string | null>(null);
  const [model, setModel] = useState("");
  const [provider, setProvider] = useState("");
  const [limit, setLimit] = useState<(typeof PAGE_SIZES)[number]>(50);
  const [page, setPage] = useState(1);

  const [expanded, setExpanded] = useState<string | null>(null);

  const filters = { page, limit, surface, model: model || null, provider: provider || null, days };
  const q = useQuery({
    queryKey: ["ai", "executions", filters],
    queryFn: () => executionsApi.list(filters),
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const surfaces = q.data?.surfaces ?? [];
  const hasNext = page * limit < total;
  const hasPrev = page > 1;

  /** Any filter change resets the pager (see above). */
  function resetTo1<T>(set: (v: T) => void) {
    return (v: T) => {
      set(v);
      setPage(1);
    };
  }

  return (
    <PageContainer>
      <PageHeader
        title="Esecuzioni"
        description="Ogni chiamata LLM (Ask, Odino, voce, chat, squadre) — la pagina Costi AI ne mostra gli aggregati; qui c’è il dettaglio per turno."
      />
      <PageContent>
      {/* Filters ----------------------------------------------------------- */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          className={inputCls}
          value={days}
          onChange={(e) => resetTo1(setDays)(Number(e.target.value) as (typeof WINDOWS)[number])}
        >
          {WINDOWS.map((w) => (
            <option key={w} value={w}>
              Ultimi {w} giorni
            </option>
          ))}
        </select>
        <select
          className={inputCls}
          value={surface ?? ""}
          onChange={(e) => resetTo1(setSurface)(e.target.value || null)}
        >
          <option value="">Tutte le superfici</option>
          {surfaces.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          className={inputCls}
          value={model}
          onChange={(e) => resetTo1(setModel)(e.target.value)}
          placeholder="Modello…"
        />
        <input
          className={inputCls}
          value={provider}
          onChange={(e) => resetTo1(setProvider)(e.target.value)}
          placeholder="Provider…"
        />
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {q.isError && !q.isLoading && (
        <p className="text-sm text-danger">Impossibile caricare le esecuzioni.</p>
      )}

      {!q.isLoading && !q.isError && rows.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Nessuna esecuzione"
          description="Nessuna esecuzione LLM nel periodo selezionato."
        />
      ) : (
        rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Data/ora</Th>
                  <Th>Superficie</Th>
                  <Th>Modello</Th>
                  <Th className="text-right">Token</Th>
                  <Th className="text-right">Output</Th>
                  <Th className="text-right">Costo</Th>
                  <Th>Utente</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <ExecRow
                    key={r.id}
                    row={r}
                    open={expanded === r.id}
                    onToggle={() => setExpanded((id) => (id === r.id ? null : r.id))}
                  />
                ))}
              </Tbody>
            </Table>
          </div>
        )
      )}

      {/* Pagination -------------------------------------------------------- */}
      {total > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{num(total)} esecuzioni</span>
            <select
              className={inputCls}
              value={limit}
              onChange={(e) => resetTo1(setLimit)(Number(e.target.value) as (typeof PAGE_SIZES)[number])}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s} per pagina
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span>Pagina {page}</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted disabled:opacity-40"
              disabled={!hasPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prec.
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted disabled:opacity-40"
              disabled={!hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Succ. <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      </PageContent>
    </PageContainer>
  );
}

/** One run row. Click-to-expand reveals the full token breakdown + the bound config
 *  ("Predefinita" when null) — kept inline (no side panel) for a read-only log. */
function ExecRow({ row, open, onToggle }: { row: ExecutionRow; open: boolean; onToggle: () => void }) {
  const cacheRead = row.cache_read_input_tokens;
  return (
    <>
      <Tr className="cursor-pointer" onClick={onToggle}>
        <Td className="whitespace-nowrap text-muted-foreground">{fmtWhen(row.created_at)}</Td>
        <Td>
          <span
            className={cn(
              "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium",
              surfaceTint(row.surface),
            )}
          >
            {row.surface}
          </span>
        </Td>
        <Td className="font-mono text-xs">{row.model}</Td>
        <Td className="text-right tabular-nums">
          {num(row.input_tokens)}
          {cacheRead > 0 && (
            <span className="ml-1 text-[10px] text-muted-foreground">+{num(cacheRead)} cache</span>
          )}
        </Td>
        <Td className="text-right tabular-nums">{num(row.output_tokens)}</Td>
        <Td className="text-right tabular-nums">${row.cost_usd.toFixed(4)}</Td>
        <Td className="text-xs text-muted-foreground">
          {row.created_by ? (
            <>
              <span className="font-mono">{row.created_by}</span>
              <span className="ml-1 opacity-70">({realmLabel(row.surface)})</span>
            </>
          ) : (
            "—"
          )}
        </Td>
      </Tr>
      {open && (
        <Tr className="bg-surface/40 hover:bg-surface/40">
          <Td colSpan={7} className="px-3 py-3 text-xs">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              <Detail label="Provider" value={row.provider} />
              <Detail label="Token input" value={num(row.input_tokens)} />
              <Detail label="Token output" value={num(row.output_tokens)} />
              <Detail label="Cache (creazione)" value={num(row.cache_creation_input_tokens)} />
              <Detail label="Cache (lettura)" value={num(row.cache_read_input_tokens)} />
              <Detail label="Costo" value={`$${row.cost_usd.toFixed(4)}`} />
              <Detail label="Configurazione" value={row.config_id ?? "Predefinita"} />
            </dl>
          </Td>
        </Tr>
      )}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
